import { Floor, Room, User } from '../types';

/** True when floorName repeats the floor number or a "Floor N" style label. */
export function isRedundantFloorName(
  floorNumber: string | number | undefined,
  floorName?: string | null
): boolean {
  const name = String(floorName ?? '').trim();
  if (!name) return true;
  const num = String(floorNumber ?? '').trim();
  if (!num) return false;
  if (name === num) return true;
  const lower = name.toLowerCase();
  if (lower === `floor ${num}`) return true;
  if (lower === `floor${num}`) return true;
  return false;
}

/** e.g. "Floor 1" or "Floor 1 — North wing" when the name adds information. */
export function formatFloorDisplay(fl: Floor): string {
  const base = `Floor ${fl.floorNumber}`;
  if (isRedundantFloorName(fl.floorNumber, fl.floorName)) return base;
  return `${base} — ${String(fl.floorName).trim()}`;
}

export function formatRoomDisplay(r: Room): string {
  return `Room ${r.roomNumber}`;
}

export type FloorRoomLookup = {
  floorById?: Record<number, Floor>;
  roomById?: Record<number, Room>;
  bedById?: Record<number, { bedNumber?: string; bedName?: string }>;
};

/** Compact label for tenant tables: "1-101". */
export function formatTenantListLocation(t: User, lookup: FloorRoomLookup): string {
  const fid = t.floor != null && String(t.floor).trim() !== '' ? Number(t.floor) : NaN;
  const rid = t.room != null && String(t.room).trim() !== '' ? Number(t.room) : NaN;
  const fl = Number.isFinite(fid) ? lookup.floorById?.[fid] : undefined;
  const rm = Number.isFinite(rid) ? lookup.roomById?.[rid] : undefined;
  const fNum = fl ? String(fl.floorNumber).trim() : '';
  const rNum = rm ? String(rm.roomNumber).trim() : '';
  if (fNum && rNum) return `${fNum}-${rNum}`;
  if (fNum) return fNum;
  if (rNum) return rNum;
  return '—';
}

/** Room number only for tenant list "Room" column. */
export function formatTenantListRoom(t: User, lookup: FloorRoomLookup): string {
  const rid = t.room != null && String(t.room).trim() !== '' ? Number(t.room) : NaN;
  const rm = Number.isFinite(rid) ? lookup.roomById?.[rid] : undefined;
  if (rm) return String(rm.roomNumber).trim();
  return '—';
}

/** Readable location from floor/room ids (e.g. daily guests). */
export function formatLocationByIds(
  floorId: number | undefined | null,
  roomId: number | undefined | null,
  lookup: FloorRoomLookup
): string {
  const pseudo = { floor: floorId ?? undefined, room: roomId ?? undefined } as User;
  return formatTenantLocationReadable(pseudo, lookup);
}

/** Readable location for reports and detail views: "Floor 1 · Room 101". */
export function formatTenantLocationReadable(t: User, lookup?: FloorRoomLookup): string {
  const parts: string[] = [];
  const fid = t.floor != null && String(t.floor).trim() !== '' ? Number(t.floor) : NaN;
  const rid = t.room != null && String(t.room).trim() !== '' ? Number(t.room) : NaN;

  if (Number.isFinite(fid) && lookup?.floorById?.[fid]) {
    parts.push(formatFloorDisplay(lookup.floorById[fid]));
  } else if (t.floor && String(t.floor).trim() && !/^\d+$/.test(String(t.floor).trim())) {
    const raw = String(t.floor).trim();
    parts.push(/^floor\s/i.test(raw) ? raw : `Floor ${raw}`);
  }

  if (Number.isFinite(rid) && lookup?.roomById?.[rid]) {
    parts.push(formatRoomDisplay(lookup.roomById[rid]));
  } else if (t.room && String(t.room).trim() && !/^\d+$/.test(String(t.room).trim())) {
    const raw = String(t.room).trim();
    parts.push(/^room\s/i.test(raw) ? raw : `Room ${raw}`);
  }

  return parts.length ? parts.join(' · ') : '—';
}

/** Dashboard floor occupancy cards. */
export function formatFloorCardLabel(f: Floor): string {
  const n = String(f.floorNumber ?? f.id);
  if (isRedundantFloorName(f.floorNumber, f.floorName)) {
    return n.match(/^\d+$/) ? `${n} floor` : n;
  }
  return `${n} — ${String(f.floorName).trim()}`;
}

/**
 * Invoice / PDF line: floor number, room number, bed number (not DB ids).
 * Pass floorById / roomById when floor & room fields store numeric ids.
 */
export function formatFloorRoomBedLine(
  fields: { floor?: string | null; room?: string | null; bed?: string | null },
  lookup?: FloorRoomLookup
): string {
  const fRaw = fields.floor != null ? String(fields.floor).trim() : '';
  const rRaw = fields.room != null ? String(fields.room).trim() : '';
  const bRaw = fields.bed != null ? String(fields.bed).trim() : '';

  const fid = fRaw !== '' ? Number(fRaw) : NaN;
  const rid = rRaw !== '' ? Number(rRaw) : NaN;

  let floorNum = '—';
  let roomNum = '—';
  let bedNum = '—';

  if (Number.isFinite(fid) && lookup?.floorById?.[fid]) {
    floorNum = String(lookup.floorById[fid].floorNumber).trim();
  } else if (fRaw) {
    floorNum = fRaw;
  }

  if (Number.isFinite(rid) && lookup?.roomById?.[rid]) {
    roomNum = String(lookup.roomById[rid].roomNumber).trim();
  } else if (rRaw) {
    roomNum = rRaw;
  }

  const bid = bRaw !== '' ? Number(bRaw) : NaN;
  if (Number.isFinite(bid) && lookup?.bedById?.[bid]) {
    const b = lookup.bedById[bid];
    bedNum = String(b.bedNumber || b.bedName || '').trim() || '—';
  } else if (bRaw) {
    bedNum = bRaw;
  }

  if (floorNum === '—' && roomNum === '—' && bedNum === '—') return '—';
  return `${floorNum}-${roomNum}-${bedNum}`;
}

/** Short floor label for complaints and property reports. */
export function formatFloorShortLabel(f: Floor): string {
  const num = String(f.floorNumber);
  if (isRedundantFloorName(f.floorNumber, f.floorName)) return num;
  return `${num} — ${String(f.floorName).trim()}`;
}
